import { useCallback, useEffect, useRef, useState } from "react";
import { Nav, Tab } from "react-bootstrap";

import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import Loader from "@/modules/shared/ui/Loader";
import {
    canAccessModelEvaluationKpi,
    DEFAULT_MODEL_EVALUATION_MODELS_FILTERS,
    fetchLatestAggregations,
    isModelEvaluationReportNotFound,
    mapShellViewModel,
    MODEL_EVALUATION_KPI_WINDOW_KEYS,
    rangeCaptionForWindow,
    type ModelEvaluationModelsFilters,
    type ModelEvaluationWindowKey,
    type ShellViewModel,
} from "@/lib/ModelEvaluationKpis";
import type { IGetLatestModelEvaluationKpiAggregationsRes } from "@/lib/backend/reports.generated.types";
import { niceDateTime } from "@/lib/util";

import AggregatedReportsTab from "./AggregatedReportsTab";
import ModelsTab from "./ModelsTab";

import "./ModelEvaluationPage.scss";

const DEFAULT_WINDOW: ModelEvaluationWindowKey = "7d";

type ActiveTab = "aggregated" | "models";

const modelEvaluationWindowTranslationId = (window: ModelEvaluationWindowKey) =>
    `Manufacturing.ModelEvaluation.window.${window}`;

export default function ModelEvaluationPage() {
    const has_access = canAccessModelEvaluationKpi();
    const [aggregations, setAggregations] = useState<IGetLatestModelEvaluationKpiAggregationsRes | null>(null);
    const [shellViewModel, setShellViewModel] = useState<ShellViewModel | null>(null);
    const [selectedWindow, setSelectedWindow] = useState<ModelEvaluationWindowKey>(DEFAULT_WINDOW);
    const [activeTab, setActiveTab] = useState<ActiveTab>("aggregated");
    const [modelsFilters, setModelsFilters] = useState<ModelEvaluationModelsFilters>(
        DEFAULT_MODEL_EVALUATION_MODELS_FILTERS
    );
    const [modelsHandoffNonce, setModelsHandoffNonce] = useState(0);
    const [loading, setLoading] = useState(has_access);
    const [empty, setEmpty] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadCancelledRef = useRef(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        setEmpty(false);
        setAggregations(null);
        setShellViewModel(null);

        try {
            const aggregations_res = await fetchLatestAggregations();

            if (!loadCancelledRef.current) {
                setAggregations(aggregations_res);
                setShellViewModel(mapShellViewModel(aggregations_res));
            }
        } catch (err: unknown) {
            if (!loadCancelledRef.current) {
                if (isModelEvaluationReportNotFound(err)) {
                    setEmpty(true);
                } else {
                    console.error((err as Error).message);
                    setError(translate("common.error"));
                }
            }
        } finally {
            if (!loadCancelledRef.current) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!has_access) {
            return;
        }

        loadCancelledRef.current = false;
        void load();
        return () => {
            loadCancelledRef.current = true;
        };
    }, [has_access, load]);

    if (!has_access) {
        return (
            <ErrorComponent
                msg={translate(
                    "Manufacturing.ModelEvaluation.no_permission",
                    "You do not have permission to view model evaluation KPIs."
                )}
                type="error"
                className="m-3"
            />
        );
    }

    if (loading) {
        return <Loader />;
    }

    if (empty) {
        return (
            <div className="model-evaluation-page">
                <h1 className="content-title">
                    {translate("Manufacturing.ModelEvaluation.title", "Model evaluation")}
                </h1>
                <ErrorComponent
                    msg={translate(
                        "Manufacturing.ModelEvaluation.no_report",
                        "No model evaluation KPI report is available yet."
                    )}
                    type="no-data"
                    className="mt-3"
                />
            </div>
        );
    }

    if (error || aggregations == null || shellViewModel == null) {
        return <ErrorComponent msg={error ?? translate("common.error")} type="error" className="m-3" />;
    }

    const window_keys =
        shellViewModel.windowKeys.length > 0 ? shellViewModel.windowKeys : [...MODEL_EVALUATION_KPI_WINDOW_KEYS];
    const range_caption = rangeCaptionForWindow(aggregations, selectedWindow);

    return (
        <div className="model-evaluation-page">
            <h1 className="content-title">{translate("Manufacturing.ModelEvaluation.title", "Model evaluation")}</h1>

            <div className="kpi-header d-flex flex-column gap-2 mb-3">
                <div className="d-flex flex-wrap justify-content-between align-items-start">
                    <div className="mb-2 mb-md-0">
                        <Nav variant="pills" className="flex-wrap" aria-label="Time window">
                            {window_keys.map(window_key => (
                                <Nav.Item key={window_key}>
                                    <Nav.Link
                                        href="#"
                                        active={selectedWindow === window_key}
                                        onClick={e => {
                                            e.preventDefault();
                                            setSelectedWindow(window_key);
                                        }}
                                    >
                                        {translate(modelEvaluationWindowTranslationId(window_key), window_key)}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                        {range_caption != null && <p className="text-muted small mb-0 mt-2">{range_caption}</p>}
                    </div>
                    {shellViewModel.createdAt != null && (
                        <span className="align-self-end text-right text-smaller text-muted">
                            {translate("common.last_update")}{" "}
                            <span className="font-weight-bold">{niceDateTime(new Date(shellViewModel.createdAt))}</span>
                        </span>
                    )}
                </div>
            </div>

            <Tab.Container activeKey={activeTab} onSelect={key => setActiveTab((key as ActiveTab) ?? "aggregated")}>
                <Nav variant="tabs" className="eval-main-tabs mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="aggregated">
                            {translate("Manufacturing.ModelEvaluation.tab.aggregated", "Aggregated reports")}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="models">
                            {translate("Manufacturing.ModelEvaluation.tab.models", "Models")}
                        </Nav.Link>
                    </Nav.Item>
                </Nav>
                <Tab.Content className="eval-main-tab-body">
                    <Tab.Pane eventKey="aggregated">
                        <AggregatedReportsTab
                            aggregations={aggregations}
                            window={selectedWindow}
                            onOpenModelsTab={handoff => {
                                setModelsFilters(handoff);
                                setModelsHandoffNonce(nonce => nonce + 1);
                                setActiveTab("models");
                            }}
                        />
                    </Tab.Pane>
                    <Tab.Pane eventKey="models">
                        <ModelsTab
                            window={selectedWindow}
                            aggregations={aggregations}
                            handoffFilters={modelsFilters}
                            handoffNonce={modelsHandoffNonce}
                        />
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
}
